import { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { Loader } from "@progress/kendo-react-indicators";
import { categories, emptyExpense, frequencies, normalizeCategory, priorities, tags } from "../../constants";
import { readFileAsDataUrl, toDateInputValue } from "../../utils/formatters";
import { localizeOptions } from "../../utils/i18n";

const toDraft = (expense) => ({
  ...emptyExpense,
  ...(expense || {}),
  category: normalizeCategory(expense?.category || emptyExpense.category),
  date: toDateInputValue(expense?.date || new Date())
});

export default function ExpenseForm({
  currencyCode,
  expense,
  isLoading,
  locale,
  onClose,
  onSave,
  onValidationError,
  t = {}
}) {
  const [draft, setDraft] = useState(toDraft(expense));
  const [fileError, setFileError] = useState("");
  const categoryOptions = localizeOptions(categories, locale, "categories");
  const priorityOptions = localizeOptions(priorities, locale, "priorities");
  const frequencyOptions = localizeOptions(frequencies, locale, "frequencies");
  const tagOptions = localizeOptions(tags, locale, "tags");
  const selectedTags = tagOptions.filter((tag) => (draft.tags || []).includes(tag.value));

  useEffect(() => {
    setDraft(toDraft(expense));
    setFileError("");
  }, [expense]);

  const updateDraft = (changes) => setDraft((current) => ({ ...current, ...changes }));

  const handleReceipt = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setFileError(t.receiptTooLarge || "Receipt must be smaller than 2 MB for local backup storage.");
      return;
    }

    const receiptDataUrl = await readFileAsDataUrl(file);
    updateDraft({
      receiptName: file.name,
      receiptDataUrl,
      receiptType: file.type
    });
    setFileError("");
  };

  const submit = () => {
    if (!draft.title.trim() || !draft.amount || Number(draft.amount) <= 0) {
      onValidationError(t.addTitleAndAmount || "Add a title and a positive amount.");
      return;
    }

    onSave(draft);
  };

  return (
    <Dialog title={expense ? t.editExpense || "Edit expense" : t.newExpense || "New expense"} onClose={onClose} width={560}>
      <div className="dialog-form">
        <div className="field full">
          <label>{t.name || "Name"}</label>
          <Input
            placeholder={t.namePlaceholder || "Groceries, rent, subscription..."}
            value={draft.title}
            onChange={(event) => updateDraft({ title: event.value })}
          />
        </div>

        <div className="field">
          <label>{(t.amount || ((currency) => `Amount (${currency})`))(currencyCode)}</label>
          <NumericTextBox
            value={draft.amount}
            min={0}
            format="n2"
            onChange={(event) => updateDraft({ amount: event.value })}
          />
        </div>

        <div className="field">
          <label>{t.date || "Date"}</label>
          <DatePicker value={draft.date} onChange={(event) => updateDraft({ date: event.value })} />
        </div>

        <div className="field">
          <label>{t.category || "Category"}</label>
          <DropDownList
            data={categoryOptions}
            textField="text"
            dataItemKey="value"
            value={categoryOptions.find((category) => category.value === draft.category) || categoryOptions[0]}
            onChange={(event) => updateDraft({ category: event.value.value })}
          />
        </div>

        <div className="field">
          <label>{t.priority || "Priority"}</label>
          <DropDownList
            data={priorityOptions}
            textField="text"
            dataItemKey="value"
            value={priorityOptions.find((priority) => priority.value === draft.priority)}
            onChange={(event) => updateDraft({ priority: event.value.value })}
          />
        </div>

        <div className="field">
          <label>{t.repeat || "Repeat"}</label>
          <DropDownList
            data={frequencyOptions}
            textField="text"
            dataItemKey="value"
            value={frequencyOptions.find((frequency) => frequency.value === draft.frequency)}
            onChange={(event) => updateDraft({ frequency: event.value.value })}
          />
        </div>

        <div className="field">
          <label>{t.tags || "Tags"}</label>
          <MultiSelect
            data={tagOptions}
            textField="text"
            dataItemKey="value"
            value={selectedTags}
            onChange={(event) => updateDraft({ tags: event.value.map((tag) => tag.value) })}
          />
        </div>

        <div className="field full">
          <label>{t.description || "Description"}</label>
          <Input
            placeholder={t.optionalNote || "Optional note"}
            value={draft.description}
            onChange={(event) => updateDraft({ description: event.value })}
          />
        </div>

        <div className="field full">
          <label>{t.receipt || "Receipt"}</label>
          <div className="file-row">
            <input accept="image/*,application/pdf" type="file" onChange={handleReceipt} />
            {draft.receiptName && (
              <Button
                type="button"
                fillMode="flat"
                icon="close"
                onClick={() => updateDraft({ receiptName: "", receiptDataUrl: "", receiptType: "" })}
              >
                {(t.removeReceipt || ((name) => `Remove ${name}`))(draft.receiptName)}
              </Button>
            )}
          </div>
          {fileError && <small className="form-error">{fileError}</small>}
        </div>
      </div>

      <DialogActionsBar>
        {isLoading && <Loader size="small" />}
        <Button onClick={onClose} disabled={isLoading}>
          {t.cancel || "Cancel"}
        </Button>
        <Button themeColor="primary" icon="save" onClick={submit} disabled={isLoading}>
          {t.save || "Save"}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
}
